const React = require('react');
const T = require('prop-types');
const { replace: Replace } = require('connected-react-router');
const { useCallback, useEffect, useMemo, useRef, useState } = require('react');
const { useDispatch } = require('react-redux');
const { useLocation } = require('react-router-dom');

const LoadingScreen = require('components/LoadingScreen');
const Snackbar = require('../../../../containers/Snackbar');

const { context } = require('app-context');
const { useSsoAuth } = require('hooks/useSsoAuth');
const { useAppSelector } = require('utils/redux');
const GetQueryParams = require('utils/get-query-params');
const AuthSso = require('services/auth-management/sso');
const AuthConstants = require('services/auth-management/constants');
const RoutingTable = require('services/auth-management/routing-table');
const { getLoginContext } = require('utils/login-context');
const { getSignupVars } = require('utils/signup-context');

const selectors = context.selectors.all;
const internals = {};

module.exports = SSOLogin;
module.exports.internals = internals;

function SSOLogin({ flow }) {

    const location = useLocation();
    const queryParams = GetQueryParams();
    const dispatch = useDispatch();

    const signupContext = useAppSelector(selectors.getSignupContext);
    const loginContext = useAppSelector(selectors.getLoginContext);
    const routeContext = flow === 'signup' ? signupContext : loginContext;
    const persistedRouteContext = useMemo(() => internals.getPersistedRouteContext({ flow }), [flow]);
    const callbackContext = AuthSso.getCallbackContext();
    const [authCode, setAuthCode] = useState(queryParams.code || null);
    const [authState, setAuthState] = useState(queryParams.state || null);

    const loginStartedRef = useRef(false);
    const callbackHandledRef = useRef(false);
    const isCallbackRequest = !!(authCode && authState);

    const isSignupState = flow === 'signup';

    const effectiveRouteContext = useMemo(() => AuthSso.getEffectiveRouteContext({
        callbackContext,
        flow,
        isCallbackRequest,
        persistedRouteContext,
        routeContext
    }), [callbackContext, flow, isCallbackRequest, persistedRouteContext, routeContext]);

    const email = effectiveRouteContext.email;
    const sso = AuthSso.normalizeSSO(effectiveRouteContext.sso);
    const provider = AuthSso.getSSOProvider(sso);

    const shouldForceSSOLoginPrompt = AuthSso.shouldForceSSOLoginPrompt({
        email,
        policy: effectiveRouteContext.policy,
        provider
    });
    const shouldForceSsoReauthentication = AuthSso.shouldForceSsoReauthentication({
        policy: effectiveRouteContext.policy,
        provider
    });

    const updateContextAction = internals.getContextUpdateAction({ flow, isSignupState });

    const redirectUri = useMemo(() => AuthSso.getSSOCallbackUri({
        provider,
        flow
    }), [flow, provider]);

    const failureMessage = internals.getFailureMessage({ flow });

    const {
        client: ssoClient,
        clearSession,
        login: loginSSO
    } = useSsoAuth({
        redirectUri,
        email,
        forceSSOLoginPrompt: shouldForceSSOLoginPrompt,
        forceSsoReauthentication: shouldForceSsoReauthentication,
        onRedirectRequestCreated: (redirectRequest) => {

            internals.persistCallbackContext({
                callbackContext,
                extraContext: { redirectRequest },
                flow,
                routeContext: effectiveRouteContext
            });
        },
        onCordovaCallbackCreated: ({ callbackUrl }) => {

            internals.persistCallbackContext({
                callbackContext,
                extraContext: { cordovaCallbackUrl: callbackUrl },
                flow,
                routeContext: effectiveRouteContext
            });
        },
        redirectRequestFallback: callbackContext?.flow === flow ? callbackContext.redirectRequest : null,
        setAuthCode,
        setAuthState,
        sso,
        onCancel: async () => {

            await redirectToOrigin();
        },
        onError: async () => {

            await redirectToOrigin({ message: failureMessage });
        }
    });

    const redirectToOrigin = useCallback(async ({ message } = {}) => {
        const redirectTarget = internals.getRedirectTarget({
            email,
            isSignupState
        });

        await clearSession();
        AuthSso.clearCallbackContext();
        loginStartedRef.current = false;
        callbackHandledRef.current = false;
        setAuthCode(null);
        setAuthState(null);

        if (message) {
            dispatch(context.actions.alerts.notification.push({ message }));
        }

        dispatch(Replace(redirectTarget.pathname, redirectTarget.state));
    }, [clearSession, dispatch, email, isSignupState]);

    useEffect(() => {

        const nextQueryParams = GetQueryParams();

        setAuthCode(nextQueryParams.code || null);
        setAuthState(nextQueryParams.state || null);
    }, [location.search]);

    useEffect(() => {
        const callbackRouteContextUpdate = AuthSso.getCallbackRouteContextUpdate({
            callbackContext,
            flow,
            isCallbackRequest,
            routeContext
        });

        if (!callbackRouteContextUpdate) {
            return;
        }

        dispatch(updateContextAction(callbackRouteContextUpdate));
    }, [callbackContext, dispatch, flow, isCallbackRequest, routeContext, updateContextAction]);

    useEffect(() => {

        if (!AuthSso.hasRequiredRouteContext({ email, redirectUri, sso })) {
            redirectToOrigin({
                message: failureMessage
            });
            return;
        }

        const login = async () => {
            await clearSession();
            internals.persistCallbackContext({
                callbackContext,
                flow,
                routeContext: effectiveRouteContext
            });

            const promptReset = AuthSso.buildSSORedirectStartedContextUpdate({
                routeContext: effectiveRouteContext
            });

            if (promptReset) {
                dispatch(updateContextAction(promptReset));
            }

            await loginSSO();
        };

        const handleRedirectCallback = async () => {

            try {
                await ssoClient.handleRedirectCallback(callbackContext?.cordovaCallbackUrl || null);
                internals.removeOAuthParamsFromUrl();

                const claims = await ssoClient.getIdTokenClaims();

                if (!claims?.__raw) {
                    throw new Error('Missing SSO token claims');
                }

                const authenticatedEmail = AuthSso.getEmailFromClaims(claims);

                if (isSignupState && !AuthSso.doesAuthenticatedEmailMatch({
                    authenticatedEmail,
                    expectedEmail: email
                })) {
                    await redirectToOrigin({
                        message: internals.getSignupEmailMismatchMessage()
                    });
                    return;
                }

                const res = await dispatch(context.actions.auth.loginSSO({
                    token: claims.__raw,
                    sso,
                    email: authenticatedEmail || undefined
                }));

                if (res) {
                    AuthSso.rememberSuccessfulSSOEmail({
                        email: authenticatedEmail || email
                    });
                }

                AuthSso.clearCallbackContext();

                if (!res) {
                    if (internals.recoverSuccessfulCallback({ dispatch })) {
                        AuthSso.rememberSuccessfulSSOEmail({
                            email: authenticatedEmail || email
                        });
                        return;
                    }

                    await redirectToOrigin({
                        message: failureMessage
                    });
                }
            }
            catch (err) {

                if (internals.recoverSuccessfulCallback({ dispatch })) {
                    AuthSso.clearCallbackContext();
                    return;
                }

                await redirectToOrigin({
                    message: failureMessage
                });
            }
        };

        if (!authState && !authCode && !loginStartedRef.current) {
            loginStartedRef.current = true;
            login();
            return;
        }

        if (authState && authCode && !callbackHandledRef.current) {
            callbackHandledRef.current = true;
            handleRedirectCallback();
        }
    }, [
        authCode,
        authState,
        callbackContext,
        clearSession,
        dispatch,
        effectiveRouteContext,
        email,
        failureMessage,
        flow,
        isSignupState,
        loginSSO,
        provider,
        redirectToOrigin,
        redirectUri,
        routeContext,
        shouldForceSSOLoginPrompt,
        sso,
        ssoClient,
        updateContextAction
    ]);

    return (
        <>
            <LoadingScreen
                variant='screen'
                message='Logging in...'
            />
            <Snackbar />
        </>
    );
}

module.exports.propTypes = {
    flow: T.oneOf(['login', 'signup']).isRequired
};

internals.removeOAuthParamsFromUrl = () => {

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('code');
    currentUrl.searchParams.delete('state');

    const nextSearch = currentUrl.searchParams.toString();
    const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`;

    window.history.replaceState({}, document.title, nextUrl);
};

internals.getContextUpdateAction = ({ flow, isSignupState }) => {

    if (isSignupState || flow === 'signup') {
        return context.actions.signup.updateContext;
    }

    return context.actions.auth.updateLoginContext;
};

internals.getFailureMessage = ({ flow }) => (
    flow === 'signup'
        ? 'We could not complete sso login for signup. Please try again.'
        : 'We could not complete sso login. Please try again.'
);

internals.getSignupEmailMismatchMessage = () => (
    'The email used for SSO does not match the email entered for signup. Please try again.'
);

internals.getLoadingMessage = () => 'Logging in...';

internals.recoverSuccessfulCallback = ({ dispatch }) => {

    const state = context.redux?.hooks?.getState?.();

    if (!state) {
        return false;
    }

    if (selectors.getIsAuthenticated?.(state)) {
        dispatch(Replace(RoutingTable.getAppLoaderRoute()));
        return true;
    }

    const signupContext = selectors.getSignupContext(state);

    if (signupContext?.hasSSOToken !== true) {
        return false;
    }

    const route = RoutingTable.getIncompleteSignupRoute({
        signupContext: {
            ...signupContext,
            hasSSOToken: true,
            isEmailVerified: true
        }
    });

    if (route === AuthConstants.authFlow.routes.signup.sso) {
        return false;
    }

    dispatch(Replace(route));
    return true;
};

internals.getRedirectTarget = ({ email, isSignupState }) => {

    if (isSignupState) {
        return {
            pathname: AuthConstants.authFlow.routes.signup.entry,
            state: undefined
        };
    }

    return {
        pathname: AuthConstants.authFlow.routes.home,
        state: email ? { email } : undefined
    };
};

internals.getPersistedRouteContext = ({ flow }) => {

    if (flow === 'signup') {
        return getSignupVars('context') || {};
    }

    return getLoginContext() || {};
};

internals.persistCallbackContext = ({
    callbackContext,
    extraContext = {},
    flow,
    routeContext
}) => {

    AuthSso.setCallbackContext({
        ...(callbackContext || {}),
        flow,
        ...AuthSso.buildRouteContextPayload(routeContext),
        ...extraContext
    });
};
